import { clsx } from 'clsx';
import { ReactNode, useLayoutEffect, useRef } from 'react';
import * as styles from './AuthFormBoxLayout.css';

type AuthFormBoxLayoutProps = {
  header?: ReactNode;
  headerClassName?: string;
  title?: ReactNode;
  subtitle?: ReactNode;
  content: ReactNode;
  cta?: ReactNode;
  footer?: ReactNode;
  align?: 'center' | 'start';
  boxed?: boolean;
  className?: string;
  bodyKey?: string;
  bodyClassName?: string;
  contentClassName?: string;
  ctaClassName?: string;
  footerClassName?: string;
};

const BOX_RESIZE_DURATION_MS = 260;
const BOX_RESIZE_EASING = 'cubic-bezier(0.25, 1, 0.5, 1)';

const isReducedMotionPreferred = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (typeof window.matchMedia !== 'function') {
    return false;
  }

  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

export const AuthFormBoxLayout = ({
  header,
  headerClassName,
  title,
  subtitle,
  content,
  cta,
  footer,
  align = 'center',
  boxed = true,
  className,
  bodyKey,
  bodyClassName,
  contentClassName,
  ctaClassName,
  footerClassName,
}: AuthFormBoxLayoutProps) => {
  const isCentered = align === 'center';
  const boxRef = useRef<HTMLElement | null>(null);
  const previousHeightRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) {
      return;
    }

    const nextHeight = box.getBoundingClientRect().height;
    const previousHeight = previousHeightRef.current;
    previousHeightRef.current = nextHeight;

    if (
      previousHeight === null ||
      previousHeight === nextHeight ||
      isReducedMotionPreferred()
    ) {
      return;
    }

    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    box.style.height = `${previousHeight}px`;
    box.style.overflow = 'hidden';
    box.style.transition = `height ${BOX_RESIZE_DURATION_MS}ms ${BOX_RESIZE_EASING}`;

    animationFrameRef.current = window.requestAnimationFrame(() => {
      box.style.height = `${nextHeight}px`;
      animationFrameRef.current = null;
    });

    const clearInlineAnimationStyles = (): void => {
      box.style.height = '';
      box.style.overflow = '';
      box.style.transition = '';
    };

    const handleTransitionEnd = (event: TransitionEvent): void => {
      if (event.propertyName !== 'height') {
        return;
      }

      clearInlineAnimationStyles();
    };

    box.addEventListener('transitionend', handleTransitionEnd);

    return () => {
      box.removeEventListener('transitionend', handleTransitionEnd);
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      clearInlineAnimationStyles();
    };
  }, [bodyKey]);

  return (
    <section
      ref={boxRef}
      data-auth-form-box
      className={clsx(styles.box, !boxed && styles.boxFrameless, className)}
    >
      {header ? (
        <header className={clsx(styles.header, headerClassName)}>
          {header}
        </header>
      ) : null}

      <div
        key={bodyKey}
        className={clsx(
          styles.body,
          isCentered ? styles.bodyCenter : styles.bodyStart,
          bodyClassName,
        )}
      >
        {title || subtitle ? (
          <div
            className={clsx(
              styles.titleBlock,
              isCentered ? styles.titleCenter : styles.titleStart,
            )}
          >
            {title}
            {subtitle}
          </div>
        ) : null}

        <div className={clsx(styles.content, contentClassName)}>{content}</div>

        {cta ? (
          <div className={clsx(styles.cta, ctaClassName)}>{cta}</div>
        ) : null}
      </div>

      {footer ? (
        <footer className={clsx(styles.footer, footerClassName)}>
          {footer}
        </footer>
      ) : null}
    </section>
  );
};
